import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MembratedLetterService } from 'src/app/_services/membratedLetter/membrated-letter.service';
import { TableConfig } from 'src/app/interfaces/bl.interfaces';
import { MembratedLetterByBatch } from 'src/app/interfaces/membratedLetter.interfaces';
import printJS from 'print-js';

/**
 * Componente que administra las cartas membretadas de un lote: lista las
 * existentes, permite crearlas por tonelaje, descargarlas, imprimirlas y
 * eliminarlas para los módulos de exportación y cabotaje.
 */
@Component({
  selector: 'app-membrated-letter',
  templateUrl: './membrated-letter.component.html',
  styleUrls: ['./membrated-letter.component.css']
})
export class MembratedLetterComponent implements OnInit {
  @Input() module: 'E' | 'C' = 'E';
  @Input() batchId: number = 0;
  table_config: TableConfig = {
    tableHeads: [
      ['Generado', 'date'],
      ['Nombre', 'pdf_name'],
      ['Tonelaje', 'total'],
      ['Generado por', 'user'],
    ],
    tableHeadsContainer: ['Imprimir', 'Descargar', 'Eliminar'],
    styles: {
      table_heads: {
        'grid-template-columns': 'repeat(4, minmax(70px, 1fr)) minmax(250px, auto)',
        'min-width': '530px',
      },
      table_row: {
        'grid-template-columns': 'repeat(4, minmax(70px, 1fr)) minmax(250px, auto)',
        'min-width': '530px',
      },
      container_icons: {
        'grid-template-columns': 'repeat(3, 1fr)',
      }
    },
    arrayFields: [
      'date',
      'pdf_name',
      'total',
      'user'
    ],
    iconos: [["/eliminar-abandono", "document_id", 'bi bi-download'], ["/actualizar-abandono", "document_id", 'bi bi-download'], ["/descargar-pedimento", "document_id", 'bi bi-trash3']],
    mensaje: "Sin registros.",
    filter: true,
    totals: {
      show: false,
      content: [],
      total: 0,
      total_percent: 0
    }
  };
  membratedLetterOverlay: boolean = false;
  createMembratedLetterOverlay: boolean = false;
  loader: boolean = false;
  loading: boolean = false;
  overlayError: boolean = false;
  overlay: boolean = false;
  buttons: boolean = false;
  message: string = '';
  icon: string = '';
  confirmDeleteOverlay: boolean = false;
  messageDelete: string = '';

  tonnageForm: FormGroup;
  membratedLetterListByBatch: MembratedLetterByBatch[] = [];
  tonnageAvailable: number = 0;
  idToDelete: number = 0;

  constructor(
    private fb: FormBuilder,
    private membratedLetterSvc: MembratedLetterService
  ) {
    this.tonnageForm = this.fb.group({
      tonnage: [, [Validators.required, Validators.pattern(/^(?!0\d)\d{0,11}(?:\.\d{1,3})?$/)]],
    });
  }

  ngOnInit(): void {
    this.loader = true;
    this.getData();
  }

  /**
   * Alterna la visibilidad del overlay con el listado de cartas membretadas.
   */
  togleMembratedLetterOverlay() {
    this.membratedLetterOverlay = !this.membratedLetterOverlay;
  }

  /**
   * Alterna la visibilidad del overlay para crear una carta membretada y reinicia el formulario al abrirlo.
   */
  togleCreateMembratedLetter() {
    this.createMembratedLetterOverlay = !this.createMembratedLetterOverlay;
    if (this.createMembratedLetterOverlay) {
      this.tonnageForm.reset();
    }
  }

  /**
   * Solicita confirmación al usuario antes de generar la carta membretada por el tonelaje capturado.
   */
  confirmCreateMembratedLetter() {
    const tonnage = this.tonnageForm.get('tonnage')?.value ?
      Number(this.tonnageForm.get('tonnage')?.value) : 0;
    this.activeOverlay(
      `Está seguro de realizar la carta por el tonelaje ${tonnage}`,
      '',
      true,
    );
  }

  /**
   * Crea la carta membretada con el tonelaje indicado, salvo que la operación se cancele o el formulario sea inválido.
   * @param cancel - Cadena no vacía cuando el usuario cancela la creación.
   */
  createMembratedLetter( cancel: string ) {
    this.overlay = false;
    if (cancel) {
      return;
    }
    if (this.tonnageForm.invalid) {
      this.tonnageForm.markAllAsTouched();
      this.activeOverlay(
        'Favor de verificar la información',
        'bi bi-exclamation-circle-fill',
        false,
        2000
      );
      return;
    }
    const tonnage = Number(this.tonnageForm.get('tonnage')?.value);
    this.loading = true;
    this.membratedLetterSvc.createMembratedLetter(this.module, this.batchId, tonnage, '')
    .subscribe({
      next: (response) => {
        this.loading = false;
        if (response.url) {
          this.activeOverlay(
            'Registro agregado correctamente',
            'bi bi-check2',
            false,
            2000
          );
          this.togleCreateMembratedLetter();
          window.open(response.url, '_blank');
          this.getData();
        } else {
          this.activeOverlay(
            'Lo sentimos no fue posible crear la carta, inténtelo más tarde',
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
        }
      }, error: () => {
        this.loading = false;
        this.overlayError = true;
      }
    });
  }

  /**
   * Descarga o imprime una carta membretada según el tipo solicitado.
   * @param id - Identificador de la carta membretada.
   * @param type - Acción a realizar: `'download'` para descargar o cualquier otro valor para imprimir.
   */
  downloadPrintMembratedLetter(id: number, type: string) {
    this.loading = true;
    this.membratedLetterSvc.getMembratedLetter(this.module, this.batchId, id)
    .subscribe({
      next: response => {
        this.loading = false;
        if (!response.url) {
          const message = type === 'download' ?
          'Lo sentimos no fue posible descargar la carta membretada, inténtelo más tarde' :
          'Lo sentimos no fue posible imprimir la carta membretada, inténtelo más tarde';
          const icon = 'bi bi-exclamation-circle-fill';
          this.activeOverlay(
            message,
            icon,
            false,
            3000
          );
          return;
        }
        if (type === 'download') {
          window.open(response.url, '_blank');
        } else {
          printJS({printable: response.base64_file, type: 'pdf', base64: true});
        }
      },
      error: (e) => {
        this.loading = false;
        this.activeOverlay(
          'Error en el servidor al descargar el archivo, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
        return;
      },
    });
  }

  /**
   * Prepara la confirmación para eliminar una carta membretada.
   * @param id - Identificador de la carta membretada a eliminar.
   */
  confirmDelete(id: number) {
    this.idToDelete = id;
    this.messageDelete = `¿Está seguro de eliminar la carta membretada: ${id}?`;
    this.confirmDeleteOverlay = true;
  }

  /**
   * Elimina la carta membretada seleccionada, salvo que la operación se cancele.
   * @param cancel - Cadena no vacía cuando el usuario cancela la eliminación.
   */
  deleteMembratedLetter( cancel: string ) {
    this.confirmDeleteOverlay = false;
    if (cancel) {
      return;
    }
    this.loading = true;
    this.membratedLetterSvc.deleteMembratedLetter(this.module, this.idToDelete)
    .subscribe({
      next: (response) => {
        this.loading = false;
        if (typeof response === 'string' && response === 'ok') {
          this.activeOverlay(
            'Carta membretada eliminada correctamente',
            'bi bi-check2',
            false,
            2000
          );
          this.getData();
        }
      }, error: () => {
        this.loading = false;
        this.overlayError = true;
      }
    });
  }

  /**
   * Carga el listado de cartas membretadas del lote y el tonelaje disponible.
   */
  getData() {
    this.loader = true;
    forkJoin([
      this.membratedLetterSvc.getMembratedLetterListByBatch(this.module, this.batchId),
      this.membratedLetterSvc.getTonnageAvailableByBatch(this.module, this.batchId)
    ]).subscribe({
      next: ([
        membratedLetterListByBatch,
        tonnageAvailable
      ]) => {
        this.membratedLetterSvc.areThereMembratedLetters = membratedLetterListByBatch.length ? true : false;
        this.membratedLetterListByBatch = [...membratedLetterListByBatch.sort((a, b) => b.document_id - a.document_id)];
        if (tonnageAvailable.length > 0) {
          this.tonnageAvailable = Number(tonnageAvailable[0].total.replace(',', ''));
        }
        this.loader = false;
      }, error: () => {
        this.loading = false;
        this.overlayError = true;
      }
    });
  }

  /**
   * Muestra un overlay con mensaje e ícono y, opcionalmente, lo oculta tras un tiempo.
   * @param message - Mensaje a mostrar.
   * @param icon - Clase del ícono a mostrar.
   * @param buttons - `true` para mostrar botones de confirmación.
   * @param time - Tiempo opcional en milisegundos tras el cual se oculta el overlay.
   */
  activeOverlay(message: string, icon: string, buttons: boolean, time?: number) {
    this.message = message;
    this.icon = icon;
    this.buttons = buttons;
    this.overlay = true;
    if (time) {
      setTimeout(() => {
        this.overlay = false;
      },time);
    }
  }
}
